import React, { useState, useEffect } from "react";
import Template from "../../components/Template";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import { formatDateWithoutHour, formatDateWithHour } from "../../utils/date";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import { maskCurrency, maskPhone, formatCurrencyFromDb } from "../../utils/masks";
import { parseCurrencyToFloat } from "../../utils/utils";
import FormLabel from "../../components/FormLabel";
import config from "../../config";

const CadastrarOrcamentos = ({ orcamentoId }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(orcamentoId);

    // Estado principal do formulário
    const [formData, setFormData] = useState({
        codigoOrcamento: "",
        editalVinculado: "",
        descricaoProjeto: "",
        valorTotal: "",
        valorEmpenho: "",
        dataEmissaoEmpenho: "",
        saldoEmpenho: "",
        percentualUso: "",
        dataInicioEmpenho: "",
        dataTerminoEmpenho: "",
        tipoDocumento: "",
        arquivoDocumento: "",
        nomeResponsavel: "",
        contatoResponsavel: "",
        observacoes: "",
        status: "",
    });

    // Anexos escolhidos em "Anexos e Documentos Complementares"
    const [anexosSelecionados, setAnexosSelecionados] = useState([]);
    const [anexosRemovidos, setAnexosRemovidos] = useState([]);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [editais, setEditais] = useState([]);
    const [obras, setObras] = useState([]);
    const [anexoFile, setAnexoFile] = useState(null);

    const [showDialog, setShowDialog] = useState(false);
    const [selectedPdf, setSelectedPdf] = useState("");
    const [listAnexoFile, setListAnexoFile] = useState([]);

    const [showConfirmDeleteRegister, setShowConfirmDeleteRegister] = useState(false);
    const [showConfirmRemoveArchiveDialog, setShowConfirmRemoveArchiveDialog] = useState(false);

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);



    // Para o anexoFile de "Anexos e Documentos Complementares"
    const handleAnexoFileChange = (e) => {
        setAnexoFile(e.target.files[0]);
    };

    const handleAddAnexo = () => {
        if (!formData.tipoDocumentoAnexo) {
            alert("Selecione um Tipo de Documento antes de enviar.");
            return;
        }
        if (!anexoFile) {
            alert("Selecione um arquivo antes de enviar.");
            return;
        }

        // Encontra no array "tiposDocumentos" o objeto do tipo selecionado
        const tipoId = parseInt(formData.tipoDocumentoAnexo, 10);
        const tipoEncontrado = tiposDocumentos.find((t) => t.id === tipoId);
        if (!tipoEncontrado) {
            alert("Tipo de documento não encontrado.");
            return;
        }

        // Cria objeto com tipo, file, data de envio, etc.
        const novoAnexo = {
            id: Date.now(), // ID local, único
            tipo: tipoId,
            tipoNome: tipoEncontrado.nome,
            file: anexoFile,
            dataEnvio: new Date().toISOString(), // ou formate como quiser
            isNew: true,
        };
        setAnexosSelecionados((prev) => [...prev, novoAnexo]);
        setListAnexoFile((prev) => [...prev, anexoFile])



        // Opcional: Resetar "Pesquisar Documento"
        setAnexoFile(null);
    };

    const handleRemoveAnexo = (anexoId) => {
        setAnexosSelecionados((prevSelecionados) => {
            // 1) Localiza o anexo
            const item = prevSelecionados.find((a) => a.id === anexoId);
            if (!item) return prevSelecionados; // se não achar, não faz nada

            // 2) Se é novo (isNew === true) ou ID temporário (p. ex.: 'temp-...'),
            //    basta remover do array principal, pois não precisa excluir no backend.
            if (item.isNew || String(item.id).startsWith('temp-')) {
                return prevSelecionados.filter((a) => a.id !== anexoId);
            } else {
                // 3) Caso contrário, movemos para anexosRemovidos,
                //    MAS evitamos duplicar caso já esteja lá.
                setAnexosRemovidos((prevRemovidos) => {
                    const jaExiste = prevRemovidos.some((r) => r.id === item.id);
                    if (!jaExiste) {
                        return [...prevRemovidos, item];
                    }
                    return prevRemovidos;
                });

                // 4) E removemos do array principal
                return prevSelecionados.filter((a) => a.id !== anexoId);
            }
        });
    };

    const handleShowPdf = (url) => {
        setSelectedPdf(url);
        setShowDialog(true);
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    // Cancelar modal
    const handleCancel = () => {
        setShowConfirmDialog(false);
    };

    // Handler específico para campo de moeda
    const handleCurrencyChange = (fieldName) => (e) => {
        const originalValue = e.target.value;
        const masked = maskCurrency(originalValue);
        setFormData((prev) => ({ ...prev, [fieldName]: masked }));
    };

    // Caso você precise carregar dados de um orcamentoId existente
    // no backend, useEffect seria algo como:
    useEffect(() => {
        // Carregar categorias e status da API
        const fetchData = async () => {
            try {
                const [tiposDocumentosRes, editaisRes, statusRes, obrasRes] = await Promise.all([
                    api.get("/tiposdocumentos"),
                    api.get("/editais/todos"),
                    api.get("/statuses/byModule/nome/orcamentos"),
                    api.get("/obras/listar/todas"),


                ]);
                setTiposDocumentos(tiposDocumentosRes.data);
                setEditais(editaisRes.data);
                setStatusList(statusRes.data);
                setObras(obrasRes.data);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };
        fetchData();
        if (isEditMode) {
            const fetchEdital = async () => {
                try {
                    const response = await api.get(`/orcamentos/${orcamentoId}`);
                    const data = response.data;

                    // No useEffect que carrega os dados do empenho:
                    const valorEmpenho = parseFloat(data.valorEmpenho) || 0;
                    const totalNotas = data.notasEmitidas?.reduce(
                        (acc, nota) => acc + (parseFloat(nota.valor) || 0),
                        0
                    ) || 0;
                    // Se não houver valorEmpenho, o saldo é zero, mesmo que haja notas emitidas.
                    const saldoCalculado = valorEmpenho ? valorEmpenho - totalNotas : 0;

                    setFormData({
                        codigoOrcamento: data.codigoOrcamento || "",
                        editalVinculado: data.editalVinculado?.id || "",
                        descricaoProjeto: data.descricaoProjeto || "",
                        valorTotal: formatCurrencyFromDb(data.valorTotal) ?? "",
                        valorEmpenho: formatCurrencyFromDb(data.valorEmpenho) ?? "",
                        dataEmissaoEmpenho: data.dataEmissaoEmpenho ? data.dataEmissaoEmpenho.split("T")[0] : "",
                        saldoEmpenho: formatCurrencyFromDb(saldoCalculado) ?? "",
                        percentualUso: data.percentualUso || "",
                        dataInicioEmpenho: data.dataInicioEmpenho ? data.dataInicioEmpenho.split("T")[0] : "",
                        dataTerminoEmpenho: data.dataTerminoEmpenho ? data.dataTerminoEmpenho.split("T")[0] : "",
                        tipoDocumento: data.tipoDocumento?.nome || "",
                        arquivoDocumento: data.arquivoDocumento?.nome || "",
                        nomeResponsavel: data.nomeResponsavel || "",
                        contatoResponsavel: data.contatoResponsavel || "",
                        observacoes: data.observacoes || "",
                        status: data.statusId || ""
                    });
                    setAnexosSelecionados(data.anexos || []);


                } catch (error) {
                    console.error("Erro ao carregar Empenho:", error);
                    alert("Erro ao carregar o Empenho. Tente novamente.");
                }
            };
            fetchEdital();
        }
    }, [isEditMode, orcamentoId]);

    /**
     * Handler do formulário principal.
     * Aqui exibimos no console, mas você pode fazer POST/PUT via axios.
     */

    // Submeter formulário
    const handleSubmit = (e) => {
        e.preventDefault();
        // if (!validateForm(formData)) return;
        setShowConfirmDialog(true);
    };

    const handleConfirm = async (e) => {
        setShowConfirmDialog(false);

        try {

            // Montamos um FormData apenas se você quiser enviar "multipart/form-data"
            const dataToSend = new FormData();

            dataToSend.append("codigoOrcamento", formData.codigoOrcamento);
            dataToSend.append("editalVinculado", formData.editalVinculado);
            dataToSend.append("descricaoProjeto", formData.descricaoProjeto);

            dataToSend.append("valorTotal", parseCurrencyToFloat(formData.valorTotal));
            dataToSend.append("valorEmpenho", parseCurrencyToFloat(formData.valorEmpenho));
            dataToSend.append("dataEmissaoEmpenho", formData.dataEmissaoEmpenho);
            dataToSend.append("saldoEmpenho", parseCurrencyToFloat(formData.saldoEmpenho));
            dataToSend.append("percentualUso", formData.percentualUso);

            dataToSend.append("dataInicioEmpenho", formData.dataInicioEmpenho);
            dataToSend.append("dataTerminoEmpenho", formData.dataTerminoEmpenho);

            // Documentos
            dataToSend.append("tipoDocumento", formData.tipoDocumento);
            dataToSend.append("arquivoDocumento", formData.arquivoDocumento); // se estiver enviando o arquivo

            // Responsável e Observações
            dataToSend.append("nomeResponsavel", formData.nomeResponsavel);
            dataToSend.append("contatoResponsavel", formData.contatoResponsavel);
            dataToSend.append("observacoes", formData.observacoes);
            dataToSend.append("status", formData.status);
            dataToSend.append("anexosAdicionados", JSON.stringify(anexosSelecionados));
            dataToSend.append("anexosRemovidos", JSON.stringify(anexosRemovidos));

            anexosSelecionados.forEach((anexo, index) => {
                dataToSend.append(`anexosFiles[${index}]`, anexo.file);
            });


            if (isEditMode) {
                await api.put(`/orcamentos/${orcamentoId}`, dataToSend, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                await api.post(`/orcamentos`, dataToSend, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            setShowSuccessDialog(true);
            setTimeout(() => {
                setShowSuccessDialog(false);
                if (isEditMode) {
                    window.location.href = `/orcamentos/editar/${orcamentoId}`;
                } else {
                    window.location.href = `/orcamentos`;
                }
            }, 3000);
        } catch (error) {
            console.error(`Erro ao ${isEditMode ? "atualizar" : "cadastrar"} Empenho:`, error);
            alert("Ocorreu um erro. Verifique os dados e tente novamente.");
        }
    };

    // Botão Voltar
    const handleBack = () => {
        // Ajuste a rota conforme sua necessidade
        navigate("/orcamentos");
    };

    // Remover arquivo
    const handleDeleteFile = async () => {
        try {
            await api.delete(`/orcamentos/${orcamentoId}/arquivo`);
            setFormData((prev) => ({ ...prev, arquivoUrl: "" }));
            setShowConfirmRemoveArchiveDialog(false);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao remover arquivo:", error);
            alert("Erro ao remover arquivo.");
        }
    };


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleDeleteRegister = async () => {
        try {
            await api.delete(`/orcamentos/${orcamentoId}`);
            setShowConfirmDeleteRegister(false);
            navigate("/orcamentos");
        } catch (error) {
            console.error("Erro ao remover Empenho:", error);
            alert("Erro ao remover Empenho.");
        }
    };

    // Handler específico para campo de telefone
    const handlePhoneChange = (e) => {
        const originalValue = e.target.value;
        const masked = maskPhone(originalValue);
        setFormData((prev) => ({ ...prev, contatoResponsavel: masked }));
    };

    return (
        <Template>
            <form onSubmit={handleSubmit} className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        {isEditMode ? "Editar Empenho" : "Cadastrar Empenho"}
                    </h1>
                </div>

                {/* Informações do Edital e Projeto */}
                <div className="w-full flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium">
                        Informações do Edital e Obra
                    </div>
                    {/* Linha 1 */}
                    <div className="flex flex-col md:flex-row gap-4 w-full">
                        {/* Código do Empenho */}
                        <div className="flex-1 flex flex-col gap-1.5">

                            <FormLabel
                                htmlFor="codigoOrcamento"
                                isRequired={true}
                            >
                                Código do Empenho
                            </FormLabel>

                            {/* Se quiser permitir edição, troque div -> input */}
                            <input
                                name="codigoOrcamento"
                                type="text"
                                value={formData.codigoOrcamento}
                                onChange={handleInputChange}
                                className="bg-white rounded-md border border-slate-300 px-3 py-2 text-slate-700 text-base"
                                required
                            />
                        </div>
                        {/* Edital Vinculado */}
                        <div className="flex-1 flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium">
                                Edital Vinculado
                            </label>
                            <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                <select
                                    name="editalVinculado"
                                    value={formData.editalVinculado}
                                    onChange={handleInputChange}
                                    className="grow bg-transparent outline-none text-slate-700 text-base"
                                >
                                    <option value="">Selecionar</option>
                                    {editais.map((objeto) => (
                                        <option key={objeto.id} value={objeto.id}>
                                            {objeto.numeroEdital}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    {/* Linha 2 */}
                    <div className="flex flex-col w-full">
                        <label className="text-slate-700 text-sm font-medium">Descrição do empenho</label>
                        <textarea
                            name="descricaoProjeto"
                            rows={2}
                            value={formData.descricaoProjeto}
                            onChange={handleInputChange}
                            className="bg-white rounded-md border border-slate-300 px-3 py-2 mt-1 text-slate-700 text-base"
                        />
                    </div>
                </div>

                {/* Dados Financeiros */}
                <div className="w-full flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium">Dados Financeiros</div>
                    {/* Linha 1 */}
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Valor do Empenho */}
                        <div className="flex-1 flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium">Valor do Empenho (R$)</label>
                            <input
                                name="valorEmpenho"
                                type="text"
                                value={formData.valorEmpenho}
                                onChange={handleCurrencyChange("valorEmpenho")}
                                className="bg-white rounded-md border border-slate-300 px-3 py-2 text-slate-700 text-base"
                            />
                        </div>
                        {/* Data de Emissão */}
                        <div className="flex-1 flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-normal">Data de Emissão do Empenho</label>
                            <input
                                name="dataEmissaoEmpenho"
                                type="date"
                                value={formData.dataEmissaoEmpenho}
                                onChange={handleInputChange}
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                            />
                        </div>
                    </div>
                    {/* Linha 2 */}
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Saldo do Empenho */}
                        <div className="flex-1 flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium">Saldo do Empenho (R$)</label>
                            <input
                                type="text"
                                name="saldoEmpenho"
                                value={formData.saldoEmpenho}
                                onChange={handleCurrencyChange("saldoEmpenho")}
                                className="bg-white rounded-md border border-slate-300 px-3 py-2 text-slate-700 text-base"
                                readOnly
                            />
                        </div>
                        {/* Percentual de Uso Permitido */}
                        <div className="flex-1 flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium">
                                Percentual de Uso Permitido (%)
                            </label>
                            <input
                                name="percentualUso"
                                type="text"
                                value={formData.percentualUso}
                                onChange={handleInputChange}
                                className="bg-white rounded-md border border-slate-300 px-3 py-2 text-slate-700 text-base"
                            />
                        </div>
                    </div>
                </div>

                {/* Prazos e Vigências */}
                <div className="w-full flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium">Prazos e Vigências</div>
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Data de Início */}
                        <div className="flex-1 flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-normal">Data de Início do Empenho</label>
                            <input
                                name="dataInicioEmpenho"
                                type="date"
                                value={formData.dataInicioEmpenho}
                                onChange={handleInputChange}
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                            />
                        </div>
                        {/* Data de Término */}
                        <div className="flex-1 flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-normal">Data do Término do Empenho</label>
                            <input
                                name="dataTerminoEmpenho"
                                type="date"
                                value={formData.dataTerminoEmpenho}
                                onChange={handleInputChange}
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                            />
                        </div>
                    </div>
                </div>

                {/* Tabela de Documentos (com scroll horizontal) */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium font-['Inter']">
                        Documentos Associados
                    </div>
                    {/* Linha de Envio de Documento */}
                    <div className="flex flex-col md:flex-row flex-wrap justify-start items-start gap-4">
                        <div className="md:w-[375.75px] w-full flex-col justify-center gap-1.5 inline-flex">
                            <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                Tipo de Documento
                            </label>
                            <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                <select
                                    name="tipoDocumentoAnexo"
                                    value={formData.tipoDocumentoAnexo}
                                    onChange={handleInputChange}
                                    className="grow bg-transparent outline-none text-slate-700 text-base"
                                >
                                    <option value="">Selecionar</option>
                                    {tiposDocumentos.map((objeto) => (
                                        <option key={objeto.id} value={objeto.id}>
                                            {objeto.nome}
                                        </option>
                                    ))}
                                </select>
                                <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                            </div>
                        </div>
                        <div className="grow flex-col gap-1.5 inline-flex">
                            <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                Selecionar Documento
                            </label>
                            <div className="flex gap-2">
                                <div className="relative flex items-center gap-4 w-full">
                                    <input
                                        name="arquivo"
                                        type="file"
                                        accept=".pdf"
                                        onChange={handleAnexoFileChange}
                                        className="absolute inset-0 opacity-0 cursor-pointer grow pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-400 text-base"

                                    />
                                    <div className={`w-full pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 flex items-center gap-2`} >
                                        <span className="text-base text-slate-400">
                                            {anexoFile ? anexoFile.name : "Pesquisar Documento"}
                                        </span>
                                    </div>
                                </div>
                                <button type="button" className="px-4 py-2 bg-stone-600 rounded-md flex justify-center items-center gap-2.5" onClick={handleAddAnexo}>
                                    <div className="text-white text-sm font-normal font-['Inter'] leading-normal">
                                        Enviar
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* Tabela de Anexos (conteúdo fixo) */}
                    <div className="w-full overflow-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Nº
                                    </th>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Tipo
                                    </th>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Data do Envio
                                    </th>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Remover
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {anexosSelecionados.length > 0 ? (
                                    anexosSelecionados.map((anexo) => (
                                        <tr key={anexo.id}>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{anexo.id}</td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{anexo?.tipoNome ?? anexo?.tipo?.nome}</td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{formatDateWithoutHour(anexo.dataEnvio)}</td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-center">
                                                {(isEditMode && anexo.id && anexo.id.toString().length < 4) && (
                                                    <button type="button" onClick={() => handleShowPdf(anexo.arquivo)} title="Ver anexo">
                                                        <img className="w-10 h-10 px-[3px] py-0.5 flex-col justify-center items-center inline-flex overflow-hidden" src="/images/icon/search.png" />
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => handleRemoveAnexo(anexo.id)} title="Remover anexo">
                                                    <img className="w-6 h-6 px-[3px] py-0.5 flex-col justify-center items-center inline-flex overflow-hidden" src="/images/icon/trash.png" />
                                                </button>

                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base text-center"
                                        >                                                Nenhum anexo enviado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Responsável e Observações */}
                <div className="w-full flex flex-col gap-8 md:flex-row">
                    <div className="flex-1 flex flex-col gap-4">
                        <div className="text-stone-600 text-base font-medium">Responsável e Observações</div>

                        {/* Nome do Responsável / Contato / Observações */}
                        <div className="flex flex-col gap-4">
                            {/* Nome do Responsável */}
                            <div className="w-full flex flex-col md:flex-row gap-4">
                                <div className="flex-1 flex flex-col gap-1.5">
                                    <label className="text-slate-700 text-sm font-medium">Nome do Responsável</label>
                                    <input
                                        name="nomeResponsavel"
                                        type="text"
                                        value={formData.nomeResponsavel}
                                        onChange={handleInputChange}
                                        className="bg-white rounded-md border border-slate-300 px-3 py-2 text-slate-700 text-base"
                                    />
                                </div>
                            </div>

                            {/* Contato */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-slate-700 text-sm font-medium">Contato</label>
                                <input
                                    name="contatoResponsavel"
                                    type="text"
                                    value={formData.contatoResponsavel}
                                    onChange={handlePhoneChange}
                                    className="bg-white rounded-md border border-slate-300 px-3 py-2 text-slate-700 text-base"
                                />
                            </div>

                            {/* Observações */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-slate-700 text-sm">Observações</label>
                                <textarea
                                    name="observacoes"
                                    rows={4}
                                    value={formData.observacoes}
                                    onChange={handleInputChange}
                                    className="bg-white rounded-md border border-slate-300 px-3 py-2 text-slate-700 text-base min-h-[120px]"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Atualizações (somente exibição) */}
                    {/*<div className="flex-1 flex flex-col gap-4 mt-4 md:mt-0">
                        <div className="text-black text-base font-medium">Atualizações</div>
                        <div className="text-slate-700 text-sm leading-tight whitespace-pre-line">

                        </div>
                    </div>*/}
                </div>
                {/* Status e Observações / Atualizações */}
                <div className="flex flex-col md:flex-row gap-8 w-full">
                    {/* Coluna 1 */}
                    <div className="md:w-1/2 w-full flex flex-col gap-4">
                        <div className="flex flex-col gap-4">
                            {/* Status / Classificação */}
                            <div className="flex flex-col md:flex-row flex-wrap gap-4">
                                <div className="md:flex-1 w-full flex-col justify-center gap-1.5">
                                    <FormLabel isRequired={true} htmlFor={'status'}>
                                        Status
                                    </FormLabel>
                                    <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            className="grow bg-transparent outline-none text-slate-700 text-base"
                                            required
                                        >
                                            <option value="">Selecionar</option>
                                            {statusList.map((objeto) => (
                                                <option key={objeto.id} value={objeto.id}>
                                                    {objeto.nome}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Botões de Ação */}
                <div className="flex items-center w-full gap-4">
                    {isEditMode && (
                        <button
                            type="button"
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={() => setShowConfirmDeleteRegister(true)}
                        >
                            <img className="w-4 h-4" src="/images/icon/trash-2.png" alt="Apagar" />
                            <span>Apagar</span>
                        </button>
                    )}

                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={handleBack}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                            <span>Voltar</span>
                        </button>

                        <button
                            type="submit"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                        >
                            <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                            <span>Salvar</span>
                        </button>
                    </div>
                </div>


                {/** MODAIS de Confirmação / Sucesso */}
                <ConfirmationModal
                    isVisible={showConfirmDialog}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    message="Deseja salvar as informações?"
                />

                <SuccessModal
                    isVisible={showSuccessDialog}
                    message="Informações salvas com sucesso!"
                    onClose={() => setShowSuccessDialog(false)}
                />

                <ConfirmationModal
                    isVisible={showConfirmRemoveArchiveDialog}
                    onConfirm={handleDeleteFile}
                    onCancel={() => setShowConfirmRemoveArchiveDialog(false)}
                    message="Deseja remover o arquivo deste edital?"
                    textButtonConfirm="Sim"
                />

                <ConfirmationModal
                    isVisible={showConfirmDeleteRegister}
                    onConfirm={handleDeleteRegister}
                    onCancel={() => setShowConfirmDeleteRegister(false)}
                    message="Deseja apagar este registro?"
                    textButtonConfirm="Sim"
                />
                {showDialog && (
                    <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden">
                        <div className=" rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                            <div className="flex justify-between items-center p-4">
                                <div className="flex items-center gap-4">
                                    <a href={config.urlArquivo(selectedPdf)} download target="_blank">
                                        <img className="w-6 h-6 relative  overflow-hidden" src="/images/icon/download.png" />
                                    </a>
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                </div>
                                <img className="w-6 h-6 relative  overflow-hidden cursor-pointer" src="/images/icon/x.png" onClick={handleCloseDialog} />
                            </div>
                            <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                                <iframe
                                    src={`${config.urlArquivo(selectedPdf)}#toolbar=0`}
                                    className="w-full h-full border-0"
                                    style={{ border: 'none' }}
                                    title="PDF Viewer"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </Template>
    );
};

export default CadastrarOrcamentos;
